// Vista de mapa para Estadísticas. Reutiliza los mismos `groups` que el
// donut/leyenda para mantener coherencia: cada fábrica es un marcador con
// un popup que muestra el controlador y, al click, abre el mismo
// GroupDetailModal que ya usa el resto de la página.
//
// Render no bloqueante: los marcadores van apareciendo conforme cada
// fábrica resuelve sus coordenadas vía factoryGeocoding (cache local +
// cola rate-limited). Si alguna fábrica no se geocodifica, simplemente no
// aparece en el mapa pero sigue en la leyenda.

import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  getFactoryCoords,
  hydrateFactoryGeocodes,
  ensureFactoryGeocodes,
  subscribeFactoryGeocodes,
  type FactoryCoords,
} from '../../services/factoryGeocoding';
import './StatsMap.css';

// Un marcador del mapa. Difiere de "Group" en que:
//  - id es único por marcador (puede haber varios con el mismo groupId si un
//    controlador tiene varias fábricas en distintas localizaciones).
//  - groupId apunta al grupo del modal a abrir cuando se hace click.
//  - factoryName es siempre el nombre de la fábrica física, usado para
//    el lookup de coordenadas — independiente del label.
export interface MapMarker {
  id: string;
  groupId: string;
  factoryName: string;
  factoryAdress?: string;
  label: string;
  sublabel?: string;
  planned: number;
  loaded: number;
  color: string;
}

interface Props {
  markers: MapMarker[];
  onMarkerTap: (groupId: string) => void;
}

// Configuración del mapa base.
//
// Antes se usaba CARTO (rastertiles/voyager). En agosto de 2026 empezaron a
// exigir API key: no dejaron de servir, pero estampan "API KEY REQUIRED" encima
// de cada tesela, así que el mapa seguía funcionando y se veía mal.
//
// CARTO "voyager": colorido suave, con carreteras y etiquetas, y el más ligero
// de los que dan esa información. Medido sobre Europa a z=6, una vista completa
// (unas 20 teselas) de cada alternativa:
//    Este (CARTO voyager).................. 222 KB
//    Esri Terrain (sin nombres ni fronteras) 185 KB
//    Esri Topo............................. 313 KB
//    Esri World Street Map................. 381 KB
//    OSM directo (el del portal Angular)... 498 KB
//    Esri Light Gray (gris, muy soso)......  76 KB
// La velocidad por tesela es la misma en todos (~140 ms), así que lo único que
// diferencia la carga es el peso.
//
// Desde agosto de 2026 CARTO exige API key: sin ella sigue sirviendo, pero
// estampa "API KEY REQUIRED" sobre cada tesela. Con la clave informada se ve
// limpio; sin ella, el mapa sigue funcionando (con el aviso) en vez de quedarse
// en blanco, que es preferible a romper la pantalla si la variable falta.
// El parámetro es `key`, NO `api_key`: con el nombre equivocado CARTO responde
// 200 igualmente, pero devuelve la tesela CON la marca de agua. Comprobado
// descargando las dos y mirándolas.
const CARTO_KEY = __CARTO_API_KEY__;
const TILE_URL = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png'
  + (CARTO_KEY ? `?key=${CARTO_KEY}` : '');
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';
const INITIAL_CENTER: [number, number] = [20, 0];   // vista mundo
const INITIAL_ZOOM = 2;

export function StatsMap({ markers, onMarkerTap }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  // True una vez que hemos encuadrado el viewport para el conjunto actual
  // de fábricas. Evita que cada nuevo marcador (los que van apareciendo
  // según resuelve el geocoding) reposicione el mapa y rompa el zoom/
  // pinch del usuario.
  const fittedRef = useRef(false);
  // Identificador del set de grupos actual: si cambia (cambio de fecha o
  // de semana), reseteamos fittedRef para volver a encuadrar.
  const groupsSignatureRef = useRef('');
  // Tick de re-render cuando el servicio emite cambios (nuevas coords).
  // Lo incluimos en las deps de placedMarkers/pendingCount para que se
  // recomputen cuando una fábrica resuelve.
  const [tick, setTick] = useState(0);

  // ── Inicializar el mapa una sola vez ──────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: INITIAL_CENTER,
      zoom: INITIAL_ZOOM,
      worldCopyJump: true,
      // En móvil queremos zoom con dos dedos; el scroll-wheel solo en escritorio.
      scrollWheelZoom: true,
    });
    L.tileLayer(TILE_URL, {
      attribution: TILE_ATTR,
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;

    // Si el componente entra montado mientras el resto del layout aún
    // se está asentando (caso típico al navegar directo a /stats con
    // chartView='map' o 'heat' en localStorage), Leaflet calcula su
    // tamaño con el contenedor a 0 y los tiles desbordan al re-pintar.
    // Le decimos al mapa que vuelva a medir cuando el contenedor cambia
    // de tamaño, y también un par de veces tras montar para cubrir el
    // caso de fuentes/animaciones que asientan el layout en frames
    // posteriores.
    const invalidate = () => {
      try { map.invalidateSize(false); } catch { /* nada */ }
    };
    const r1 = requestAnimationFrame(invalidate);
    const t1 = setTimeout(invalidate, 120);
    const t2 = setTimeout(invalidate, 400);
    const observer = new ResizeObserver(() => invalidate());
    observer.observe(containerRef.current);

    return () => {
      cancelAnimationFrame(r1);
      clearTimeout(t1);
      clearTimeout(t2);
      observer.disconnect();
      // Aborta cualquier animación pendiente antes de destruir el mapa.
      // Leaflet programa un setTimeout(250ms) de respaldo en _animateZoom
      // que llama _onZoomTransitionEnd y, si el mapPane ya está fuera del
      // DOM, intenta leer `_leaflet_pos` sobre undefined (error reportado
      // en Sentry). Marcando _animatingZoom=false hacemos que esa función
      // diferida haga early-return y no acceda al pane.
      try {
        (map as unknown as { _animatingZoom?: boolean })._animatingZoom = false;
        map.stop();
        map.off();
      } catch { /* nada */ }
      map.remove();
      mapRef.current = null;
      markersRef.current.clear();
    };
  }, []);

  // ── Suscripción al store de geocoding ────────────────────────────────
  useEffect(() => {
    return subscribeFactoryGeocodes(() => setTick((n) => n + 1));
  }, []);

  // ── Hidratar cache + lanzar geocoding cuando cambian los marcadores ──
  useEffect(() => {
    // Si el conjunto de marcadores ha cambiado (cambio de fecha/semana o
    // de modo de agrupación), permitimos volver a encuadrar el viewport.
    const sig = markers.map((m) => m.id).sort().join('|');
    if (sig !== groupsSignatureRef.current) {
      groupsSignatureRef.current = sig;
      fittedRef.current = false;
    }
    let cancelled = false;
    (async () => {
      // Geocodificamos por factoryName (no por label, que en modo
      // controlador es un email). Deduplicamos para no pedir varias veces
      // la misma fábrica si el toggle agrupa por controlador y un mismo
      // sitio aparece en varias filas.
      const uniqueFactories = new Map<string, { factoryName: string; factoryAdress?: string }>();
      for (const m of markers) {
        if (!uniqueFactories.has(m.factoryName)) {
          uniqueFactories.set(m.factoryName, { factoryName: m.factoryName, factoryAdress: m.factoryAdress });
        }
      }
      const names = Array.from(uniqueFactories.keys());
      await hydrateFactoryGeocodes(names);
      if (cancelled) return;
      // Disparar lookups solo de los que no estén ya resueltos. El conteo
      // de "pendientes" se calcula derivado en cada render — así
      // decrementa automáticamente conforme cada fábrica resuelve.
      const todo = Array.from(uniqueFactories.values()).filter(
        (x) => getFactoryCoords(x.factoryName) === undefined
      );
      if (todo.length === 0) return;
      await ensureFactoryGeocodes(todo);
    })();
    return () => { cancelled = true; };
  }, [markers]);

  // ── Calcular marcadores a partir de los marcadores + coords ──────────
  const placedMarkers = useMemo(() => {
    const out: Array<{ m: MapMarker; coords: FactoryCoords }> = [];
    for (const m of markers) {
      const c = getFactoryCoords(m.factoryName);
      if (c && c.lat != null && c.lng != null) out.push({ m, coords: c });
    }
    return out;
    // tick fuerza re-cálculo cuando emit() dispara desde el servicio.
  }, [markers, tick]);

  // Conteo de fábricas pendientes en cuenta regresiva. Una fábrica está
  // "pendiente" si su entrada en el cache aún es undefined (no se ha
  // intentado o está en cola). En cuanto resuelve (hit o miss), sale de
  // este conteo.
  const pendingCount = useMemo(() => {
    const seen = new Set<string>();
    let n = 0;
    for (const m of markers) {
      if (seen.has(m.factoryName)) continue;
      seen.add(m.factoryName);
      if (getFactoryCoords(m.factoryName) === undefined) n++;
    }
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, tick]);

  // ── Sincronizar marcadores con el mapa ────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const seen = new Set<string>();

    // Posiciones de display: si varias fábricas/controladores caen en la
    // misma coordenada (típico en modo controlador, donde varios controladores
    // comparten una misma fábrica física), las desplegamos en abanico para que
    // no queden pins solapados (uno encima de otro) y se vean todos con su color.
    const positions = spreadPositions(map, placedMarkers);

    for (const { m } of placedMarkers) {
      seen.add(m.id);
      const pos = positions.get(m.id)!;
      const existing = markersRef.current.get(m.id);
      const pct = m.planned > 0 ? Math.round((m.loaded / m.planned) * 100) : 0;
      // Dirección de la fábrica (si la tiene), quitando el "(controlador)" final.
      const addr = (m.factoryAdress ?? '').replace(/\s*\([^)]*\)\s*$/, '').trim();
      const addrHtml = addr
        ? `<div class="stats-map-popup__addr">
             <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"/></svg>
             <span>${escapeHtml(addr)}</span>
           </div>`
        : '';
      const popupHtml = `
        <div class="stats-map-popup">
          <div class="stats-map-popup__title" style="border-color:${m.color}">${escapeHtml(m.label)}</div>
          ${m.sublabel ? `<div class="stats-map-popup__sub">${escapeHtml(m.sublabel)}</div>` : ''}
          ${addrHtml}
          <div class="stats-map-popup__row"><span>Planificados</span><strong>${m.planned}</strong></div>
          <div class="stats-map-popup__row"><span>Cargados</span><strong>${m.loaded}</strong></div>
          <div class="stats-map-popup__row"><span>Progreso</span><strong>${pct}%</strong></div>
          <button class="stats-map-popup__btn" data-group-id="${escapeAttr(m.groupId)}">Ver detalle</button>
        </div>
      `;

      if (existing) {
        existing.setLatLng(pos);
        existing.setIcon(buildIcon(m.color, pct));
        existing.bindPopup(popupHtml);
      } else {
        const lm = L.marker(pos, { icon: buildIcon(m.color, pct) }).addTo(map);
        lm.bindPopup(popupHtml);
        lm.bindTooltip(m.sublabel ? `${m.label} · ${m.sublabel}` : m.label, { direction: 'top', offset: [0, -8] });
        lm.on('popupopen', (ev) => {
          const node = ev.popup.getElement();
          if (!node) return;
          const btn = node.querySelector<HTMLButtonElement>('.stats-map-popup__btn');
          btn?.addEventListener('click', () => onMarkerTap(m.groupId), { once: true });
        });
        markersRef.current.set(m.id, lm);
      }
    }

    // Quitar marcadores que ya no aplican (cambio de fecha/modo → otros).
    for (const [id, marker] of markersRef.current) {
      if (!seen.has(id)) {
        marker.remove();
        markersRef.current.delete(id);
      }
    }

    // Ajustar viewport SOLO la primera vez que aparece algún marcador
    // para este conjunto. Si después llegan más marcadores (porque siguen
    // geocodificándose), no movemos el mapa — así el usuario no pierde
    // el zoom. Cuando cambia la fecha/semana o el modo, fittedRef se
    // resetea en el useEffect de markers.
    if (placedMarkers.length > 0 && !fittedRef.current) {
      const bounds = L.latLngBounds(placedMarkers.map(({ coords }) => [coords.lat, coords.lng] as [number, number]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6, animate: true });
      fittedRef.current = true;
    }

    // El abanico se calcula en píxeles para el zoom actual, así que hay que
    // recolocar los pins solapados cada vez que cambia el zoom (si no, al
    // alejar se separarían demasiado y al acercar volverían a pisarse).
    const reposition = () => {
      const spread = spreadPositions(map, placedMarkers);
      for (const { m } of placedMarkers) {
        const mk = markersRef.current.get(m.id);
        const p = spread.get(m.id);
        if (mk && p) mk.setLatLng(p);
      }
    };
    map.on('zoomend', reposition);
    return () => { map.off('zoomend', reposition); };
  }, [placedMarkers, onMarkerTap]);

  return (
    <div className="stats-map" data-no-pull-refresh>
      <div ref={containerRef} className="stats-map__canvas" />
      {pendingCount > 0 && (
        <div className="stats-map__pending">
          Localizando {pendingCount} {pendingCount === 1 ? 'fábrica' : 'fábricas'}…
        </div>
      )}
      {markers.length > 0 && placedMarkers.length === 0 && pendingCount === 0 && (
        <div className="stats-map__empty">
          No se han podido localizar las fábricas en el mapa.
        </div>
      )}
    </div>
  );
}

// Calcula la posición de display de cada marcador. Los marcadores que caen en
// la misma coordenada (misma fábrica física) se despliegan en abanico alrededor
// del punto para que se vean todos en vez de quedar apilados uno sobre otro.
// El offset se calcula en píxeles sobre el zoom actual, por eso hay que
// recalcularlo en cada 'zoomend'.
function spreadPositions(
  map: L.Map,
  placed: Array<{ m: MapMarker; coords: FactoryCoords }>
): globalThis.Map<string, [number, number]> {
  const zoom = map.getZoom();
  // Agrupar por coordenada (redondeada para tolerar micro-diferencias).
  const groups = new globalThis.Map<string, Array<{ m: MapMarker; coords: FactoryCoords }>>();
  for (const item of placed) {
    const key = `${item.coords.lat.toFixed(4)},${item.coords.lng.toFixed(4)}`;
    const arr = groups.get(key);
    if (arr) arr.push(item);
    else groups.set(key, [item]);
  }
  const result = new globalThis.Map<string, [number, number]>();
  for (const arr of groups.values()) {
    if (arr.length === 1) {
      const { m, coords } = arr[0];
      result.set(m.id, [coords.lat, coords.lng]);
      continue;
    }
    // Radio del abanico en píxeles: crece con el número de pins para que la
    // separación entre vecinos se mantenga (~34px, algo menos que el pin).
    const n = arr.length;
    const spacing = 34;
    const r = Math.max(20, spacing / (2 * Math.sin(Math.PI / n)));
    const base = map.project([arr[0].coords.lat, arr[0].coords.lng], zoom);
    arr.forEach((item, i) => {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2; // primer pin arriba
      const ll = map.unproject([base.x + r * Math.cos(angle), base.y + r * Math.sin(angle)], zoom);
      result.set(item.m.id, [ll.lat, ll.lng]);
    });
  }
  return result;
}

function buildIcon(color: string, pct: number): L.DivIcon {
  // Marker SVG circular con relleno = color del marcador, label encima
  // con el % cargado.
  const size = 36;
  const html = `
    <div class="stats-map-marker" style="--c:${color}">
      <div class="stats-map-marker__pin">
        <span class="stats-map-marker__pct">${pct}%</span>
      </div>
    </div>
  `;
  return L.divIcon({
    html,
    className: 'stats-map-marker-wrap',
    iconSize: [size, size + 6],
    iconAnchor: [size / 2, size + 4],
    popupAnchor: [0, -size + 4],
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    c === '&' ? '&amp;' :
    c === '<' ? '&lt;' :
    c === '>' ? '&gt;' :
    c === '"' ? '&quot;' : '&#39;'
  ));
}
function escapeAttr(s: string): string {
  return escapeHtml(s);
}
